import { useEffect, useRef, useState } from 'react';
import { SimulationConfig, Emitter } from '../components/WavePool/types';

interface WavePoolAudioNodes {
    osc: OscillatorNode;
    gain: GainNode;
}

export const useWavePoolAudio = (
    config: SimulationConfig, 
    emitters: Emitter[]
) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const oscillatorsRef = useRef<Map<number, WavePoolAudioNodes>>(new Map());
    const masterGainRef = useRef<GainNode | null>(null);

    const toggleAudio = async () => {
        if (!isPlaying) {
            // Init Audio Context on user gesture
            if (!audioCtxRef.current) {
                const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
                audioCtxRef.current = new AudioContext();
                
                // Master Gain (Volume limit)
                const master = audioCtxRef.current.createGain();
                master.gain.value = 0.15; // Reduced volume to prevent clipping
                master.connect(audioCtxRef.current.destination);
                masterGainRef.current = master;
            }

            if (audioCtxRef.current.state === 'suspended') {
                await audioCtxRef.current.resume();
            }
            setIsPlaying(true);
        } else {
            if (audioCtxRef.current) {
                audioCtxRef.current.suspend();
            }
            setIsPlaying(false);
        }
    };

    // Sync with Wave Pool state
    useEffect(() => {
        if (!audioCtxRef.current || !masterGainRef.current || !isPlaying) return;
        const ctx = audioCtxRef.current;
        const now = ctx.currentTime;

        // Scale Wave Pool frequencies (0.01-0.5 Hz) to audible range (4-200 Hz)
        const FREQ_SCALE = 400;
        const carrier = config.baseFrequency ?? config.frequency;

        // Collect all frequency sources
        const frequencySources: { freq: number; amp: number; index: number }[] = [];
        
        // Add base simulation frequency (carrier)
        frequencySources.push({
            freq: carrier * FREQ_SCALE,
            amp: config.amplitude * 0.5, // Scale amplitude
            index: 0
        });

        // Add active emitters
        emitters.forEach((emitter, idx) => {
            if (emitter.active) {
                frequencySources.push({
                    freq: emitter.frequency * FREQ_SCALE,
                    amp: config.amplitude * 0.3, // Slightly lower for harmonics
                    index: idx + 1
                });
            }
        });

        // Remove oscillators that no longer exist
        const currentIndices = new Set(frequencySources.map(s => s.index));
        oscillatorsRef.current.forEach((nodes, index) => {
            if (!currentIndices.has(index)) {
                try {
                    nodes.osc.stop();
                    nodes.osc.disconnect();
                    nodes.gain.disconnect();
                } catch (e) {
                    // Ignore already stopped errors
                }
                oscillatorsRef.current.delete(index);
            }
        });

        // Create or update oscillators
        frequencySources.forEach(source => {
            let nodes = oscillatorsRef.current.get(source.index);

            if (!nodes) {
                // Create new oscillator
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                
                osc.type = 'sine';
                osc.connect(gain);
                gain.connect(masterGainRef.current!);
                osc.start();

                nodes = { osc, gain };
                oscillatorsRef.current.set(source.index, nodes);
            }

            // Update parameters
            nodes.osc.frequency.setTargetAtTime(source.freq, now, 0.05);
            nodes.gain.gain.setTargetAtTime(source.amp, now, 0.05);
        });

    }, [config, emitters, isPlaying]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (audioCtxRef.current) {
                audioCtxRef.current.close();
            }
        };
    }, []);

    return { isPlaying, toggleAudio };
};
